import React from "react";
import { usePlan } from "../../contexts/PlanContext";
import { Users, FolderKanban, HardDrive, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ProgressBar = ({ current, limit, color }) => {
    if (limit === -1) return null;
    const pct = limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
    const isNear = pct >= 80;
    const isOver = pct >= 100;
    return (
        <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
            <div
                className={`h-full rounded-full transition-all ${isOver ? "bg-red-500" : isNear ? "bg-amber-500" : color}`}
                style={{ width: `${pct}%` }}
            />
        </div>
    );
};

const UsageRow = ({ icon: Icon, label, current, limit, color }) => {
    const isUnlimited = limit === -1;
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon className="w-3 h-3" />
                    <span>{label}</span>
                </div>
                <span className="font-mono font-medium text-foreground">
                    {current}{isUnlimited ? "" : `/${limit}`}
                </span>
            </div>
            {!isUnlimited && <ProgressBar current={current} limit={limit} color={color} />}
        </div>
    );
};

export const UsageCounter = ({ collapsed = false }) => {
    const { planData, loading } = usePlan();
    const navigate = useNavigate();

    if (loading || !planData) return null;

    const usage = planData.usage || {};
    const limits = planData.limits || {};

    if (collapsed) {
        const userPct = limits.users !== -1 && limits.users > 0 ? (usage.users / limits.users) * 100 : 0;
        const projPct = limits.projects !== -1 && limits.projects > 0 ? (usage.projects / limits.projects) * 100 : 0;
        const isNear = userPct >= 80 || projPct >= 80;
        return (
            <button
                onClick={() => navigate("/settings/subscription")}
                className={`w-full flex items-center justify-center p-2 rounded-lg transition-colors ${isNear ? "bg-amber-500/10 text-amber-500" : "bg-primary/5 text-primary"} hover:bg-primary/10`}
                title={`${planData.plan_name} - ${usage.users}/${limits.users === -1 ? "∞" : limits.users} utilisateurs`}
                data-testid="usage-counter-collapsed"
            >
                <Crown className="w-4 h-4" />
            </button>
        );
    }

    return (
        <div
            className="mx-3 p-3 rounded-lg bg-muted/50 space-y-3 cursor-pointer hover:bg-muted/80 transition-colors"
            onClick={() => navigate("/settings/subscription")}
            data-testid="usage-counter"
        >
            <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-foreground">{planData.plan_name}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                    {planData.subscription_status === "trialing" ? "Essai" : planData.subscription_status === "active" ? "Actif" : ""}
                </span>
            </div>
            <UsageRow
                icon={Users}
                label="Utilisateurs"
                current={usage.users || 0}
                limit={limits.users ?? -1}
                color="bg-blue-500"
            />
            <UsageRow
                icon={FolderKanban}
                label="Projets"
                current={usage.projects || 0}
                limit={limits.projects ?? -1}
                color="bg-violet-500"
            />
            <UsageRow
                icon={HardDrive}
                label="Stockage"
                current={usage.storage_gb || 0}
                limit={limits.storage_gb ?? -1}
                color="bg-green-500"
            />
        </div>
    );
};
